import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminShell from "@/components/admin/AdminShell";
import api from "@/lib/api";
import { Eye, Download, StickyNote, Trash2, History, RotateCcw } from "lucide-react";

const ACTION_META = {
  view_audit: { label: "Viewed audit", Icon: Eye, cls: "text-slate-300 bg-white/5" },
  export_csv: { label: "Exported CSV", Icon: Download, cls: "text-amber-300 bg-amber-400/10" },
  refund_note_added: { label: "Added refund note", Icon: StickyNote, cls: "text-emerald-300 bg-emerald-400/10" },
  refund_note_deleted: { label: "Deleted refund note", Icon: Trash2, cls: "text-rose-300 bg-rose-400/10" },
  reset_family: { label: "Reset family data", Icon: RotateCcw, cls: "text-rose-300 bg-rose-400/10" },
};

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AdminActivity() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/activity?limit=200")
      .then(({ data }) => { setRows(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <AdminShell subtitle="Team accountability" title="Admin activity log">
      <div className="rounded-2xl bg-[#151737] border border-white/10 overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-300 text-sm"><History size={14} className="text-amber-300" /> Most recent {rows.length} events</div>
        </div>
        {loading && <div className="px-5 py-8 text-slate-400 text-sm">Loading…</div>}
        {!loading && rows.length === 0 && <div className="px-5 py-10 text-center text-slate-400 text-sm" data-testid="admin-activity-empty">No activity recorded yet.</div>}
        <ul className="divide-y divide-white/5" data-testid="admin-activity-list">
          {rows.map((a) => {
            const m = ACTION_META[a.action] || { label: a.action, Icon: History, cls: "text-slate-300 bg-white/5" };
            const Icon = m.Icon;
            return (
              <li key={a.id} className="px-5 py-3.5 flex items-center gap-4" data-testid={`admin-activity-row-${a.id}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${m.cls}`}>
                  <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white">
                    <span className="font-semibold">{m.label}</span>
                    {a.child_id && (
                      <>
                        <span className="text-slate-500"> · </span>
                        <Link to={`/admin/families/${a.child_id}`} className="text-amber-300 hover:underline">{a.child_name || a.child_id}</Link>
                      </>
                    )}
                    {a.detail && <span className="text-slate-400"> ({a.detail})</span>}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{a.admin_email}</div>
                </div>
                <div className="text-xs text-slate-400 whitespace-nowrap">{fmt(a.created_at)}</div>
              </li>
            );
          })}
        </ul>
      </div>
    </AdminShell>
  );
}
